import Vue from "vue";
import VueI18n from "vue-i18n";
import { ENGLISH_LOCALE } from "./constants";

const messages = {
  en: {
    generic: {
      acceptTermsOfUse:
        'I accept the <a href="https://bisgraf.bonanza.se/docs/licenseagreement/Bisgraf_License_Agreement.pdf" target="_blank">terms of use</a>',
      yourEmailAddress: "Your email address",
      privacyPolicy:
        '<a href="http://bisgraf.bonanza.se/docs/gdpr/Personal_Data_Policy_EN.pdf" target="_blank">Learn more</a> about how we use and protect your data',
      contactUs: "Contact us",
      readMore: "Read more"
    },
    navigation: {
      about: "This is Bisgraf",
      purchase: "Purchase",
      contact: "Contact",
      login: "Login"
    },
    modal: {
      close: "Close"
    },
    comparePlansModal: {
      columnTitles: ["Plan feature set", "Trial", "Premium", "Pro"],
      feature: {
        visualiseBalanceSheetAndProfitLoss:
          "Visualize balance sheet and profit & loss account",
        multipleHistoricFinancialStatements:
          "Show multiple historic financial statements",
        manualCompany: "Create companies manually",
        visualiseCashFlow: "Visualize cash flow",
        manualFinancialStatements: "Add financial statements manually",
        compareCompanies: "Compare companies",
        changeAmounts: "Change amounts",
        saveChanges: "Save changes",
        keyRatios: "Key ratios",
        trends: "Trends",
        simulation: "Simulate multiple periods/scenarios",
        industryStatistics: "Industry comparison (Swedish companies only)",
        analysisGuide: "Analysis guide"
      }
    },
    salesPitch: {
      heading: "The Visual Language of Finance",
      text:
        "With Bisgraf, you can visualize financial statements to understand faster and more easily communicate a company's financial situation. With historical financial statements, you can quickly see how the company has developed. What happens in the future? With the simulation function, it becomes clear how future plans affect the balance sheet and income statement as well as cash flow.",
      video: "https://www.youtube.com/embed/bfJNFXBHYY0",
      wantToKnowMore:
        "Want to know more about the Visual Language of Finance? Visit ",
      tryForFree: "Try for free, 5 days",
      tryForFreeButtonText: "Try for free",
      successTitle: "Thank you for signing up!",
      successText:
        "We are happy that you want to use Bisgraf! We have sent you an activation mail. Just click the link in the activation mail to get started!",
      somethingWentWrongTitle: "Something went wrong...",
      somethingWentWrongText: "The email adress is already in use."
    },
    about: {
      heading: "This is Bisgraf",
      video: "https://player.vimeo.com/video/318053103",
      content1Section1:
        "Financial information is relevant to many, but if you do not understand it, or take the time to understand it, it is still of no use!\n" +
        "The economy should be understandable to everyone!",
      content1Section2:
        "When looking at finances, you often see heavy Excel documents on income and balance sheets with numbers, numbers, numbers. Bisgraf is a tool that easily and quickly converts hundreds of thousands of financial statements into visual graphs.",
      content1Section3:
        "Use Bisgraf at your next presentation, as a discussion paper for upcoming meetings or to find new business opportunities."
    },
    purchaseLicense: {
      heading: "Purchase license",
      headingUpdate: "Change license",
      cancelSubscription: "Cancel subscription",
      cancelSubscriptionText:
        "The subscription will be canceled when the license period expires",
      cancelSubscriptionSuccessTitle: "Subscription canceled",
      cancelSubscriptionSuccessText:
        "Your license is valid until the end of the license period",
      referenceCode: "Reference code",
      submit: "Submit",
      trialButton: "Start your free trial now!",
      subscription: "Subscription",
      vatExcluded:
        "All prices are stated excluding VAT and refer to subscriptions with a 12-month license period. The subscription can be canceled at any time in Bisgraf under My account/Settings/Subscription. The subscription ends when the current license period has expired.",
      selectLicense: "Select license",
      comparePlans: "Compare plans",
      from: "from",
      selectOneOrMoreDatabases: "Select one or more databases",
      bizzdoReadMoreLink: "https://bizzdo.se/ext/bizzdo-datakalla-bisgraf",
      orderSummary: "Order summary",
      subtotal: "Subtotal",
      vat: "VAT",
      total: "Total",
      interval: {
        monthlyPrice: "per month",
        yearlyPrice: "per year",
        monthly: "Monthly",
        yearly: "Yearly"
      },
      proceedToCheckout: "Proceed to checkout",
      registerForFree: "Register for free",
      updateSubscription: "Update subscription",
      responses: {
        createSuccessTitle: "Your account has been created!",
        createSuccess:
          "You have received an email with a link to activate your account.",
        updateSuccessTitle: "Your license has been renewed!"
      },
      couponCodePlaceholder: "Coupon code",
      applyCoupon: "Apply",
      couponValidating: "Validating...",
      couponApplied: "Applied",
      invalidCoupon: "Invalid coupon code",
      errorValidatingCoupon: "Error validating coupon code",
      couponAppliedSuccessful:
        'The coupon code "{code}" has been applied. Your new price is shown in the next step.'
    },
    footer: {
      information: {
        powered:
          "Bisgraf® is powered by Bonanza’s method </br> The Visual Language of Finance® trusted by:",
        partners:
          "Through our partners, millions of financial statements for hundreds of thousands of companies are made available in Bisgraf®:",
        uc:
          "All limited companies in Sweden, including consolidated financial statements",
        morningstar: "Listed company globally",
        bizzdo:
          "All limited companies in Sweden, including consolidated financial statements and data from ERP-systems for affiliated companies",
        copyright: "©Bonanzagraf AB – All rights reserved."
      }
    }
  },
  sv: {
    generic: {
      acceptTermsOfUse:
        'Jag accepterar <a href="https://bisgraf.bonanza.se/docs/licenseagreement/Bisgraf_License_Agreement.pdf" target="_blank">Allmänna villkor</a>',
      yourEmailAddress: "Fyll i din e-postadress",
      privacyPolicy:
        '<a href="https://bisgraf.bonanza.se/docs/gdpr/Personuppgiftspolicy-SE.pdf" target="_blank">Läs mer</a> om hur vi använder och skyddar dina personuppgifter',
      contactUs: "Kontakta oss",
      readMore: "Läs mer"
    },
    navigation: {
      license: "Licenser",
      about: "Så fungerar Bisgraf",
      contact: "Kontakta oss",
      login: "Logga in"
    },
    modal: {
      close: "Stäng fönster"
    },
    comparePlansModal: {
      columnTitles: ["Funktioner", "Trial", "Premium", "Pro"],
      feature: {
        visualiseBalanceSheetAndProfitLoss:
          "Visualisera balans- och resultaträkning",
        multipleHistoricFinancialStatements: "Visa flera historiska bokslut",
        manualCompany: "Skapa företag manuellt",
        visualiseCashFlow: "Visualisera kassaflöde",
        manualFinancialStatements: "Lägga till bokslut manuellt",
        compareCompanies: "Jämföra bolag",
        changeAmounts: "Ändra beloppstorlek",
        saveChanges: "Spara ändringar",
        keyRatios: "Nyckeltal",
        trends: "Trender",
        simulation: "Simulering, flera perioder/scenarios",
        industryStatistics: "Branschjämförelse",
        analysisGuide: "Analysguide, för effektiv bolagsanalys"
      }
    },
    salesPitch: {
      heading: "Pedagogiska ekonomirapporter",
      text:
        "Trött på obegripliga siffertabeller? Med Bisgraf visualiserar du ekonomirapporten och kan snabbt och enkelt förstå och kommunicera företagets ekonomi.",
      video: "https://www.youtube.com/embed/bfJNFXBHYY0",
      includesHeader: "Detta ingår:",
      competitionAnalysis:
        "<b>Konkurrentanalys</b><br>Jämför med en konkurrent eller hela branschen för att se styrkor och svagheter.",
      quarterlyMonthlyData:
        "<b>Års-, kvartals, och månadsdata</b><br>Det går att läsa in bokföringsdata till Bisgraf. Våra olika licenser innehåller dessutom olika externa datakällor.",
      simulateFunction:
        "<b>Simuleringsfunktion</b><br>Med simuleringsfunktionen kan du provtrycka ekonomin och se hur förändringar påverkar framtida balans-, resultaträkningar och kassaflöden.",
      wantToKnowMore:
        'Vill du veta mer? <a href="https://bonanza.se/bisgraf" target="_blank">Klicka här</a>',
      tryForFree: "Prova gratis, 5 dagar",
      tryForFreeButtonText: "Prova gratis",
      successTitle: "Tack!",
      successText:
        "Vi är glada att du vill använda Bisgraf! Ett aktiveringsmejl har skickats till dig där du bara klickar på länken för att komma igång.",
      somethingWentWrongTitle: "Något gick fel...",
      somethingWentWrongText: "E-postadressen är redan registrerad.",
      acceptTermsRequiredText: "Du måste acceptera Allmänna villkor!"
    },
    about: {
      heading: "Så fungerar Bisgraf",
      video: "https://www.youtube.com/embed/ReULfcBQlT8",
      content1Section1: "Bisgraf är en webbapplikation",
      content1Section2:
        "Det betyder att det enda du behöver är en internetuppkoppling och en webbläsare. Bisgraf kan köras i alla moderna webbläsare men är optimerad för Google Chrome.",
      content2Section1: "Bokslutsdatabaser",
      content2Section2:
        "Bisgraf hanterar bokslutsdata men innehåller ingen data i sig själv. Därför måste Bisgraf kopplas till en databas som innehåller bokslutsdata. Du kan välja en eller flera databaser i din licens. Bisgraf kan läsa in data från utvalda leverantörer av publika bokslut. Du kan också läsa in realtidsdata från ditt bokföringsprogram för att analysera den senaste månadsrapporten.",
      content3Section1: "Datasäkerhet",
      content3Section2:
        "All data som hanteras i Bisgraf är säker! Om du läser in data från en bokslutsdatabas så laddas den till Bisgraf när du använder appen. Det gäller både publika årsbokslut och månadsbokslut direkt från bokföringen. När du stänger av Bisgraf finns ingen data kvar i appen. Om du lägger in bokslut manuellt, simulerar eller gör andra inmatningar så kan du spara till fil. Filen laddas ned till din hårddisk innanför eventuella brandväggar och kan hanteras som vilken fil som helst. Inget sparas i molnet!"
    },
    purchaseLicense: {
      heading: "Skaffa din licens",
      headingUpdate: "Ändra licens",
      trialButton: "Påbörja din kostnadsfria provperiod idag!",
      referenceCode: "Referenskod",
      submit: "Ange",
      subscription: "Abonnemang",
      vatExcluded:
        "Alla priser anges exkl moms och avser abonnemang med 12 månaders licensperiod. Abonnemanget kan när som helst sägas upp i Bisgraf under Mitt konto/Inställningar/Abonnemang. Abonnemanget avslutas när innevarande licensperiod har gått ut.",
      selectLicense: "Välj licens",
      comparePlans: "Jämför planer",
      from: "från",
      selectOneOrMoreDatabases: "Välj en eller flera databaser",
      bizzdoReadMoreLink: "https://bizzdo.se/ext/bizzdo-datakalla-bisgraf",
      orderSummary: "Order summering",
      subtotal: "Delsumma",
      vat: "Moms",
      total: "Summa",
      interval: {
        monthlyPrice: "per månad",
        yearlyPrice: "per år",
        monthly: "Månadsvis",
        yearly: "Årsvis"
      },
      proceedToCheckout: "Fortsätt till betalning",
      registerForFree: "Registrera dig gratis",
      updateSubscription: "Uppdatera abonnemang",
      updateNote: `
        <p>När du ändrar din prenumeration till en konfiguration med ett lägre pris kommer ändringarna effektueras fr o m nästa licensperiod som då debiteras med det lägre priset.</p>
        <p>Om den nya konfigurationen representerar ett högre pris kommer ändringarna effektueras direkt och du debiteras mellanskillnaden för de återstående dagarna i den innevarande licensperioden.</p>
      `,
      cancelSubscription: "Avsluta abonnemang",
      cancelSubscriptionText:
        "Abonnemanget avslutas när denna licensperiod har gått ut",
      cancelSubscriptionSuccessTitle: "Abonnemanget avslutat",
      cancelSubscriptionSuccessText:
        "Din licens är giltig till licensperioden har gått",
      responses: {
        createSuccessTitle: "Ditt konto har skapats!",
        createSuccess:
          "Du har fått ett mejl med länk för att aktivera ditt konto.",
        successCTA: "Logga in",
        updateSuccessTitle: "Din licens har förnyats!",
        updateFailureTitle: "Uppgraderingen misslyckades",
        updateFailureText:
          "Betaltjänsten Stripe lyckades inte genomföra transaktionen. Titta i Inkorgen för att se om du har fått mejl från Stripe."
      },
      couponCodePlaceholder: "Kampanjkod",
      applyCoupon: "Använd",
      couponValidating: "Validerar...",
      couponApplied: "Använd",
      invalidCoupon: "Ogiltig kampanjkod",
      errorValidatingCoupon: "Fel vid validering av kampanjkod",
      couponAppliedSuccessful:
        'Kampanjkoden "{code}" har lagts till. Ditt nya pris syns i nästa steg.'
    },
    footer: {
      information: {
        powered:
          "Bisgraf® baseras på Bonanzas metod </br> The Visual Language of Finance® som bl a används av:",
        partners:
          "Genom våra samarbetspartner görs miljontals bokslut för hundratusentals bolag tillgängliga i Bisgraf:",
        uc: "Alla aktiebolag i Sverige inkl koncernbokslut",
        morningstar: "Noterad bolag globalt",
        bizzdo:
          "Alla aktiebolag i Sverige inkl koncernbokslut samt data från bokföring för anslutna företag",
        copyright: "©Bonanzagraf AB – Alla rättigheter förbehållna."
      }
    }
  }
};

Vue.use(VueI18n);

const i18n = new VueI18n({
  locale: ENGLISH_LOCALE,
  messages
});

export default i18n;
